import {
  Button,
  Col,
  Form,
  Layout,
  Menu,
  Dropdown,
  Row,
  Table,
  Tabs,
  Tag,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatNumbers,
  formatString,
  handleChangePage,
  handlePageSizeChange,
  handleExportToExcel,
  handleExportToPDF,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { Check, ChevronDown, Eye, Pen, Trash, TriangleAlert } from "lucide-react";
import ConfirmationModal from "../../Components/CustomModals/ConfirmationModal";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";
import ReasonModal from "../../Components/CustomModals/ReasonModal";

const { TabPane } = Tabs;

function renderStatusTag(status: any) {
  const formattedStatus = (status || "").toLowerCase();
  let color = "default";
  let label = status || "Unknown";

  switch (formattedStatus) {
    case "active":
      color = "#34D399";
      label = "Active";
      break;
    case "cancelled":
      color = "#F87171";
      label = "Cancelled";
      break;
    case "cancel_request":
      color = "#f99a56ff";
      label = "Cancel Request";
      break;
  }

  return <Tag color={color}>{label}</Tag>;
}

const CreditBillingStatement = () => {
  const [loading,        setLoading]        = useState("");
  const [showModal,      setShowModal]      = useState("");
  const [filterConfig,   setFilterConfig]   = useState<any>({ page: 1, page_size: 10, status: "active" });
  const [dataSize,       setDataSize]       = useState(0);
  const [selectedRow,    setSelectedRow]    = useState<any>({});
  const [dataSource,     setDataSource]     = useState<any[]>([]);
  const [activeTab,      setActiveTab]      = useState("active");
  const [action,         setAction]         = useState("");
  const [errorMessage,   setErrorMessage]   = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [reason,         setReason]         = useState("");

  function clearFields() {
    setSelectedRow({});
    setReason("");
  }

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({ ...prev, status: activeKey, page: 1 }));
  }

  function getBaseColumns() {
    return [
      {
        title: "Billing No.",
        dataIndex: "id",
        key: "id",
      },
      {
        title: "Company Name",
        dataIndex: "company_name",
        key: "company_name",
      },
      {
        title: "Statement No.",
        dataIndex: "statement_no",
        key: "statement_no",
      },
      {
        title: "Amount",
        dataIndex: "2307_calculation",
        key: "2307_calculation",
        align: "right" as const,
        render: (value: any) => <span>{formatNumbers(value)}</span>,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: renderStatusTag,
        width: 130,
      },
    ];
  }

  function getTableColumns() {
    const base = getBaseColumns();

    switch (activeTab) {
      case "active":
        return [
          ...base,
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="view"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo("/credit-billing-statement/review/" + record.id)}
                    >
                      Review
                    </Menu.Item>
                    <Menu.Item
                      key="edit"
                      icon={<Pen strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo("/credit-billing-statement/edit/" + record.id)}
                    >
                      Edit
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];

      case "cancel_request":
        return [
          ...base,
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="view"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo("/credit-billing-statement/approval/" + record.id)}
                    >
                      Review
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];

      case "":
      case "cancelled":
      default:
        return [
          ...base,
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="view"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo("/credit-billing-statement/view/" + record.id)}
                    >
                      View
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];
    }
  }

  async function exportToExcel() {
    setLoading("export");
    try {
      const response = await getRequest(`credit_billings/get`, { ...filterConfig, page: "" }, true);
      if (response.success) {
        handleExportToExcel(response.data.data, getBaseColumns(), "Credit Billing Statements");
      } else {
        setErrorMessage("Failed to fetch data for export.");
      }
    } catch {
      setErrorMessage("Error exporting data.");
    } finally {
      setLoading("");
    }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const response = await getRequest(`credit_billings/get`, { ...filterConfig, page: "", page_size: "" }, true);
      if (response.success) {
        const data = response.data.data;
        handleExportToPDF(
          data,
          getBaseColumns(),
          "CreditBillingStatements.pdf",
          { title: "List of Credit Billing Statements", content: `Generated on ${new Date().toLocaleString()}` },
          `Total Records: ${data.length}`
        );
      } else {
        setErrorMessage("Failed to fetch data for PDF export.");
      }
    } catch {
      setErrorMessage("Error exporting PDF.");
    } finally {
      setLoading("");
    }
  }

  const paginationConfig = {
    current:          filterConfig.page,
    pageSize:         filterConfig.page_size,
    total:            dataSize,
    showSizeChanger:  true,
    onChange:         (page: number) => handleChangePage(page, setFilterConfig),
    onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size, setFilterConfig),
  };

  async function submitConfirm() {
    setLoading("confirm");
    try {
      const response = await postRequest(
        `credit_billings/change_status`,
        {
          credit_billing_id: selectedRow.id,
          action,
          reason,
        },
        true
      );

      if (response.success) {
        setSuccessMessage(formatString(response.data.message));
        setShowModal("");
        clearFields();
        fetchTableData();
      } else {
        setErrorMessage(
          formatString(
            response?.data?.response?.data?.messages?.error_messages?.[0] ??
              `Failed to ${action.replace(/_/g, " ")} credit billing statement.`
          )
        );
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading("");
    }
  }

  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    try {
      const response = await getRequest(`credit_billings/get`, filterConfig, true);
      if (response.success) {
        setDataSize(response.data.size);
        setDataSource(response.data.data);
      } else {
        setDataSize(0);
        setDataSource([]);
      }
    } catch {
      setErrorMessage("Error loading data. Please try again.");
    } finally {
      setLoading("");
    }
  }

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Credit Billing Statement"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Credit Billing Statement"
              onCreate={() => navigateTo("/credit-billing-statement/add")}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={exportToExcel}
              onExportToPDF={exportToPDF}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value, page: 1 }))
              }
            />
          </Col>

          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="custom-table min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="Active" key="active">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="Pending Cancel Requests" key="cancel_request">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="Cancelled" key="cancelled">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="All" key="">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={loading === "fetch"}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      <ConfirmationModal
        title={action ? `Are you sure you want to ${action.replace(/_/g, " ")} this credit billing statement?` : "Confirm action"}
        footer={
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={() => { setShowModal(""); clearFields(); }}>Cancel</Button>
            <Button
              type="primary"
              loading={loading === "confirm"}
              onClick={submitConfirm}
            >
              Confirm
            </Button>
          </div>
        }
        icon={<TriangleAlert className="text-orange-400" size={60} />}
        showModal={showModal === "confirm"}
        onCancel={() => { setAction(""); setSelectedRow({}); setShowModal(""); }}
      />

      <ReasonModal
        title="Are you sure you want to cancel this credit billing statement? Add your reason below."
        footer={
          <div className="flex justify-center gap-3 mt-5">
            <Button onClick={() => { setShowModal(""); setReason(""); }}>Cancel</Button>
            <Button
              type="primary"
              loading={loading === "confirm"}
              disabled={!reason.trim()}
              onClick={submitConfirm}
            >
              Confirm
            </Button>
          </div>
        }
        icon={<TriangleAlert className="text-orange-400" size={60} />}
        showModal={showModal === "reason"}
        onCancel={() => { setShowModal(""); setReason(""); }}
        reasonValue={reason}
        onReasonChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setReason(e.target.value)
        }
      />

      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />
      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default CreditBillingStatement;